import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/coupons`;

/**
 * Coupon Service
 * Gerencia todas as operações de cupons
 */
class CouponService {
  /**
   * Get all coupons
   */
  async getAllCoupons() {
    try {
      const response = await api.get(API_BASE);
      return unwrapList(response);
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      console.error("❌ Erro ao buscar cupons:", error);
      throw error;
    }
  }

  /**
   * Get coupon by ID
   */
  async getCouponById(id) {
    try {
      const response = await api.get(
        `${API_BASE}/${id}`
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao buscar cupom:", error);
      throw error;
    }
  }

  /**
   * Validate coupon by code
   */
  async validateCoupon(code) {
    try {
      const response = await api.get(
        `${API_BASE}/${code}/validate`
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao validar cupom:", error);
      throw error;
    }
  }

  /**
   * Create new coupon
   */
  async createCoupon(couponData) {
    try {
      const response = await api.post(
        API_BASE,
        couponData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar cupom:", error);
      throw error;
    }
  }

  /**
   * Update coupon
   */
  async updateCoupon(id, couponData) {
    try {
      const response = await api.put(
        `${API_BASE}/${id}`,
        couponData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao atualizar cupom:", error);
      throw error;
    }
  }

  /**
   * Delete coupon
   */
  async deleteCoupon(id) {
    try {
      const response = await api.delete(
        `${API_BASE}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("❌ Erro ao deletar cupom:", error);
      throw error;
    }
  }

  /**
   * Activate coupon
   */
  async activateCoupon(id) {
    try {
      const response = await api.put(
        `${API_BASE}/${id}/activate`,
        {}
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao ativar cupom:", error);
      throw error;
    }
  }

  /**
   * Deactivate coupon
   */
  async deactivateCoupon(id) {
    try {
      const response = await api.put(
        `${API_BASE}/${id}/deactivate`,
        {}
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao desativar cupom:", error);
      throw error;
    }
  }

  /**
   * Format date for display (DD/MM/YYYY)
   */
  formatDate(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  }

  /**
   * Format date for input (YYYY-MM-DD)
   */
  formatDateForInput(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  }

  /**
   * Check if coupon is expired
   */
  isExpired(expirationDate) {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  }

  /**
   * Get status badge info
   */
  getStatusBadge(coupon) {
    if (!coupon.active) {
      return { variant: "destructive", label: "Inativo" };
    }
    if (this.isExpired(coupon.expiration_date)) {
      return { variant: "secondary", label: "Expirado" };
    }
    return { variant: "success", label: "Ativo" };
  }

  /**
   * Validate coupon data
   */
  validateCouponData(data) {
    const errors = [];

    if (!data.code || data.code.trim().length === 0) {
      errors.push("Código do cupom é obrigatório");
    }

    if (data.code && data.code.length > 20) {
      errors.push("Código deve ter no máximo 20 caracteres");
    }

    if (!data.discount_percentage || data.discount_percentage <= 0) {
      errors.push("Desconto deve ser maior que zero");
    }

    if (data.discount_percentage > 100) {
      errors.push("Desconto não pode ser maior que 100%");
    }

    if (!data.expiration_date) {
      errors.push("Data de expiração é obrigatória");
    }

    return errors;
  }
}

export default new CouponService();
